/**
 * useTheme — applies the resolved GRIDBOX theme to <html data-theme> and,
 * while the user is still on the OS-following "system" source, keeps it in
 * sync with `prefers-color-scheme` changes. Mount once near the app root.
 *
 * The initial attribute is set before paint by the inline script in
 * index.html; this hook takes over once React mounts. Returns the active
 * theme so callers (e.g. the sonner <Toaster>) can match it.
 */
import { useEffect } from "react"
import { useUiStore } from "../stores/useUiStore"

export function useTheme() {
  const theme = useUiStore((s) => s.theme)
  const themeSource = useUiStore((s) => s.themeSource)

  useEffect(() => {
    document.documentElement.dataset.theme = theme
  }, [theme])

  useEffect(() => {
    if (themeSource !== "system" || !window.matchMedia) return
    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    const apply = () => useUiStore.getState().setSystemTheme(mq.matches ? "dark" : "light")
    apply()
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [themeSource])

  return theme
}
